import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'

import * as styles from './PlanListIndexFrame.module.scss'

import {
  BannerHurusatoNouzeiSmall,
  Loader,
  LoginModal,
  MainColumn,
  MultiColumn,
  NotFound,
  SearchLayout,
  SearchMainContent,
  SearchPanel,
  ShowAllToggle,
  SideColumn,
  SoldOutRecommend,
  Sort,
} from '../../components'
import { HotelPlanItem } from '../../components/plans/HotelPlanItem'
import { PATH } from '../../constants'
import {
  useCreateNextPath,
  useHotelPlansApi,
  usePropertyProvider,
  useSearchQueryParams,
  useStorageEnabled,
} from '../../hooks'

const sortNamespace = 'components.Sort'
const emptyTranslateNamespace = 'empty.planNotFound'

/**
 * Landing page for the plan-first entry (`/:property/plans`).
 *
 * Lists the facility's public plans (no room scope) via `getHotelPlans`; each
 * plan card's CTA navigates to `/:property/:plan/rooms` (`PlanRoomIndexFrame`)
 * to pick a room. Wrapped in the same 2-column search shell as `RoomIndexFrame`
 * / `PlanRoomIndexFrame` so the layout matches the rest of the search flow.
 *
 * - `SearchPanel` is given `destinationPath={PATH.propertyPlans}` so date /
 *   guest / room-count refinements re-fetch ON this page instead of bouncing to
 *   `/:property/rooms` (Codex P2). Property changes still escape to
 *   `/:property/rooms` via SearchPanel's internal `onPropertyChange`.
 * - Sort (recommendation / price / -price) and the available-only toggle are
 *   applied server-side via `getHotelPlans` (parity with the room-scoped
 *   `PlanIndex`).
 * - On a 400 (malformed params) / 404 (unknown hotel) we redirect to the shared
 *   NotFound page; on 5xx to the server-error page (parity with
 *   `PlanRoomIndexFrame`).
 *
 * See docs/specs/2026-06-05-property-plans-index-design.md §4.3.
 */
export const PlanListIndexFrame: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { property: propertySlug } = useParams<{ property: string }>()
  const { params } = useSearchQueryParams()
  const { onCreateNextPath } = useCreateNextPath()
  const { isActivity } = usePropertyProvider()
  const storageEnabled = useStorageEnabled()

  const planOrder =
    params.planOrder === 'price' ||
    params.planOrder === '-price' ||
    params.planOrder === 'recommendation'
      ? params.planOrder
      : 'recommendation'

  const { data, error, isLoading } = useHotelPlansApi({
    propertySlug: propertySlug ?? '',
    checkInDate: params.checkInDate,
    checkOutDate: params.checkOutDate,
    roomCount: params.roomCount,
    numberOfAdults: params.numberOfAdults,
    numberOfChildren: params.numberOfChildren,
    numberOfInfants: params.numberOfInfants,
    order: planOrder,
    showAllPlans: params.showAll,
  })

  useEffect(() => {
    const status = (error as { response?: { status?: number } } | undefined)
      ?.response?.status
    if (status === 400 || status === 404) {
      navigate(PATH.errors.notFoundError, { replace: true })
    } else if (typeof status === 'number' && status >= 500) {
      navigate(PATH.errors.serverError, { replace: true })
    }
  }, [error, navigate])

  if (!propertySlug) {
    return null
  }

  // 施設のプランが0件＝この施設・条件では予約できない。プラン個別
  // (/:property/:plan/rooms)が「そのプランのみ満室」なのと違い、施設として予約不可
  // なので満室レコメンド（週カレンダー＋近隣施設＋横断検索）を出す。
  //
  // ただし「予約可能なもののみ表示」トグルOFF（showAll=true）は「全部見せて」という
  // 明示要求。それでも0件なら満室ではなく純粋に該当プランが無い状態なので、満室
  // レコメンドへは置き換えず従来の NotFound に倒す（RoomIndex と一貫した方針）。
  const emptyContent = params.showAll ? (
    <NotFound
      title={t(`${emptyTranslateNamespace}.attention`)}
      description1={t(`${emptyTranslateNamespace}.direction`)}
    />
  ) : (
    <SoldOutRecommend />
  )

  return (
    <SearchLayout>
      {!storageEnabled && <LoginModal isOpen onClose={() => {}} nextPath="#" />}
      <SearchMainContent>
        <MultiColumn>
          <SideColumn>
            <SearchPanel destinationPath={PATH.propertyPlans} />
            <div className={clsx(styles.bannerArea, 'pc')}>
              {isActivity ? null : <BannerHurusatoNouzeiSmall />}
            </div>
          </SideColumn>
          <MainColumn>
            {isLoading ? (
              <Loader />
            ) : (
              <React.Fragment>
                <Sort
                  count={data?.length ?? 0}
                  onChange={(e) => {
                    navigate(
                      onCreateNextPath(PATH.propertyPlans, {
                        queryParams: { planOrder: e.target.value },
                      })
                    )
                  }}
                  defaultValue={planOrder}
                  noMargin
                >
                  <option value="recommendation">
                    {t(`${sortNamespace}.list.recommendation`)}
                  </option>
                  <option value="price">
                    {t(`${sortNamespace}.list.ascPrice`)}
                  </option>
                  <option value="-price">
                    {t(`${sortNamespace}.list.descPrice`)}
                  </option>
                </Sort>
                <ShowAllToggle />
                {data &&
                  (data.length === 0 ? (
                    emptyContent
                  ) : (
                    <ul>
                      {data.map((plan) => (
                        <li className={styles.item} key={plan.id ?? plan.slug}>
                          <HotelPlanItem {...plan} />
                        </li>
                      ))}
                    </ul>
                  ))}
              </React.Fragment>
            )}
            <div className={clsx(styles.bannerArea, 'sp')}>
              {isActivity ? null : <BannerHurusatoNouzeiSmall />}
            </div>
          </MainColumn>
        </MultiColumn>
      </SearchMainContent>
    </SearchLayout>
  )
}
